// Popover anclado a un botón, portaleado al body.
//
// Por qué portalear y no un `absolute` dentro de la card: cada sección de la ficha
// es una card con `overflow-hidden` (lo necesita para que el hover del encabezado
// respete las esquinas redondeadas), así que un menú posicionado adentro se corta
// justo en el borde. Y aunque se le sacara el overflow a esa card, el panel de
// contenido de escritorio es `position: fixed` y vuelve a recortar
// (project_fixed_panel_stacking_trap).
//
// Al portalear al body la posición ya no puede ser relativa: se calcula desde el
// rectángulo del botón y se recalcula al scrollear o redimensionar. El scroll se
// escucha en CAPTURA porque en escritorio no scrollea el body sino un contenedor
// interno (ADR-2026-08-…-dual-scroll-container): sin capture, el menú se queda
// clavado mientras la página se mueve debajo.
import { useCallback, useEffect, useLayoutEffect, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';

/** Separación entre el botón y el panel, y respiro contra el borde de pantalla. */
const GAP = 8;
const MARGEN = 12;

export interface PopoverAncladoProps {
  abierto: boolean;
  /** El elemento al que se ancla. Normalmente el botón que lo abre. */
  anclaRef: RefObject<HTMLElement | null>;
  onCerrar: () => void;
  /** De qué lado del ancla se alinea. */
  alineacion?: 'izquierda' | 'derecha';
  /** Ancho del panel en píxeles. */
  ancho?: number;
  /**
   * Sin el velo que cierra al tocar afuera. Es para los paneles que se abren al
   * pasar el mouse: el velo tapa la pantalla entera y el `mouseleave` del ancla
   * nunca llegaría, así que el panel quedaría abierto para siempre.
   */
  sinVelo?: boolean;
  /** `menu` para los que se tocan; `tooltip` para los que solo informan. */
  rol?: 'menu' | 'tooltip';
  /**
   * Sobre qué capa se dibuja. El default alcanza para la pantalla, pero un
   * popover abierto DENTRO de un `SheetLateral` (z-210/211) quedaría detrás del
   * panel: los dos se portalean al body, así que compiten en el mismo
   * apilamiento y gana el número más alto.
   */
  capa?: number;
  /**
   * El contenido maneja su propio scroll (un buscador fijo arriba y una lista que scrollea): el
   * panel no scrollea y es una columna flex que corta en el alto disponible. Sin esto, un hijo que
   * scrollea adentro de un panel que también scrollea da DOS barras: el borde del panel suma 2 px
   * al alto del hijo y el panel desborda.
   */
  scrollPropio?: boolean;
  children: React.ReactNode;
}

interface Posicion {
  left: number;
  /** Se ancla por ARRIBA o por ABAJO según hacia dónde se abre. */
  top?: number;
  bottom?: number;
  /** Alto disponible: el panel nunca tapa más de lo que hay. */
  maxHeight: number;
}

export default function PopoverAnclado({
  abierto,
  anclaRef,
  onCerrar,
  alineacion = 'izquierda',
  ancho = 240,
  sinVelo,
  rol = 'menu',
  capa = 200,
  scrollPropio = false,
  children,
}: PopoverAncladoProps) {
  const [pos, setPos] = useState<Posicion | null>(null);

  const calcular = useCallback(() => {
    const el = anclaRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const alto = window.innerHeight;
    const abajo = alto - r.bottom - GAP - MARGEN;
    const arriba = r.top - GAP - MARGEN;
    // Se abre hacia abajo salvo que arriba haya bastante más lugar.
    const haciaArriba = abajo < 220 && arriba > abajo;

    let left = alineacion === 'derecha' ? r.right - ancho : r.left;
    left = Math.min(Math.max(MARGEN, left), window.innerWidth - ancho - MARGEN);

    setPos(
      haciaArriba
        ? // Anclado por abajo: si se anclara por arriba, el panel crecería hacia
          // abajo desde el borde del botón y lo taparía.
          { bottom: alto - r.top + GAP, left, maxHeight: Math.max(160, arriba) }
        : { top: r.bottom + GAP, left, maxHeight: Math.max(160, abajo) },
    );
  }, [anclaRef, alineacion, ancho]);

  // `useLayoutEffect` para que la primera pintura ya salga en su lugar: con
  // `useEffect` el panel aparece un frame en (0,0) y se ve el salto.
  useLayoutEffect(() => {
    if (!abierto) {
      setPos(null);
      return;
    }
    calcular();
  }, [abierto, calcular]);

  useEffect(() => {
    if (!abierto) return;
    const alMover = () => calcular();
    // `true` = captura: atrapa también el scroll de los contenedores internos.
    window.addEventListener('scroll', alMover, true);
    window.addEventListener('resize', alMover);
    const alaTecla = (e: KeyboardEvent) => e.key === 'Escape' && onCerrar();
    document.addEventListener('keydown', alaTecla);
    return () => {
      window.removeEventListener('scroll', alMover, true);
      window.removeEventListener('resize', alMover);
      document.removeEventListener('keydown', alaTecla);
    };
  }, [abierto, calcular, onCerrar]);

  if (!abierto || !pos) return null;

  return createPortal(
    <>
      {/* Velo invisible: cerrar al tocar afuera sin depender de dónde se tocó. */}
      {!sinVelo && (
        <div className="fixed inset-0" style={{ zIndex: capa - 10 }} onMouseDown={onCerrar} />
      )}
      <div
        role={rol}
        className={`fixed rounded-xl border border-neutral-200 bg-white shadow-xl ${
          scrollPropio ? 'flex flex-col overflow-hidden' : 'overflow-auto'
        }`}
        style={{
          zIndex: capa,
          top: pos.top,
          bottom: pos.bottom,
          left: pos.left,
          width: ancho,
          maxHeight: pos.maxHeight,
          // Un panel que solo informa no atrapa el mouse: si lo hiciera, el
          // puntero entrando en él dispararía el `mouseleave` del ancla y el
          // panel parpadearía al pasar por encima.
          pointerEvents: rol === 'tooltip' ? 'none' : undefined,
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}
